import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routesAliases, buildProvisionedRoleVersionRequirementsPath } from 'router/routesAliases';
import { useAsyncFn } from 'react-use';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import { ROLES } from 'appConstants';
import {
  createJobTaskVersion,
  deleteJobTaskVersion,
  getJobTaskVersions,
  getJobTaskVersionRequirements,
  updateJobTaskVersion,
  launchJobTaskVersion,
  cancelJobTaskVersion,
} from 'http/jobTaskVersions';
import {
  createCompanyJobRoleVersion,
  deleteCompanyJobRoleVersion,
  getCompanyJobRoleVersions,
  getCompanyJobRoleVersionRequirements,
  updateCompanyJobRoleVersion,
  launchCompanyJobRoleVersion,
  cancelCompanyJobRoleVersion,
} from 'http/companyProvisioning';
import {
  createProvisionedJobRoleVersion,
  deleteProvisionedJobRoleVersion,
  getProvisionedJobRoleVersions,
  getProvisionedJobRoleVersionRequirements,
  updateProvisionedJobRoleVersion,
  launchProvisionedJobRoleVersion,
  cancelProvisionedJobRoleVersion,
} from 'http/hyperAdminProvisioned';
import CompanySyncPanel from 'components/CompanySyncPanel';
import { actions as modalActions } from 'redux/modal';
import {
  getConfirmationModalConfig,
  openEntityLifecycleConfirm,
  resolveApiErrorMessage,
  transformTableData,
} from 'utils';
import getVersionsTableConfig from './helpers/getVersionsTableConfig';
import JobTaskVersionForm from './JobTaskVersionForm';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  versionModal: {
    '& .MuiDialog-paper': {
      overflow: 'visible',
    },
    '& .MuiDialogContent-root': {
      overflow: 'visible',
      minHeight: theme.spacing(46),
      paddingBottom: theme.spacing(14),
    },
  },
  addVersionTooltip: {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    maxWidth: 320,
    borderRadius: theme.spacing(1),
  },
}));

const SORT_FIELD_API_MAP = {
  versionCode: 'versionCodeSort',
  revision: 'revisionSort',
  status: 'statusSort',
  effectiveFrom: 'effectiveFrom',
  updatedAt: 'updatedAt',
};

const DEFAULT_SORTING = {
  field: 'effectiveFrom',
  dir: 'desc',
};

const DELETABLE_VERSION_STATUSES = ['draft', 'scheduled'];

const JobTaskVersionsTable = ({ jobTaskId, isFromSystem = false, provisionedCompanyId = null }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userRole = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = userRole === ROLES.HYPER;
  const isSuperAdmin = userRole === ROLES.SUPER;
  const isProvisionedContext =
    Number.isFinite(Number(provisionedCompanyId)) && Number(provisionedCompanyId) > 0;
  const canViewVersions = isProvisionedContext || isHyperAdmin || isSuperAdmin;
  const canMutateVersions = isProvisionedContext || isHyperAdmin || (isSuperAdmin && !isFromSystem);
  const useSystemVersionApis = !isProvisionedContext && isHyperAdmin && !isFromSystem;
  const tableConfig = getVersionsTableConfig();
  const [sorting, setSorting] = useState(DEFAULT_SORTING);

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const buildSortParams = useCallback(({ field, dir }) => {
    const apiField = SORT_FIELD_API_MAP[field];
    if (!apiField) {
      return {};
    }

    return {
      [apiField]: dir.toUpperCase(),
    };
  }, []);

  const [versionsState, fetchVersions] = useAsyncFn(async () => {
    const params = {
      jobTaskId,
      page: 0,
      pageSize: 100,
      ...buildSortParams(sorting),
    };

    if (isProvisionedContext) {
      return getProvisionedJobRoleVersions(Number(provisionedCompanyId), jobTaskId, params);
    }

    if (useSystemVersionApis) {
      return getJobTaskVersions(params);
    }

    return getCompanyJobRoleVersions(jobTaskId, params);
  }, [jobTaskId, sorting, buildSortParams, isProvisionedContext, provisionedCompanyId, useSystemVersionApis]);

  const refreshVersions = useCallback(() => {
    fetchVersions();
  }, [fetchVersions]);

  useEffect(() => {
    if (canViewVersions && jobTaskId) {
      fetchVersions();
    }
  }, [canViewVersions, jobTaskId, fetchVersions]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [createState, createVersion] = useAsyncFn(
    async payload => {
      const result = isProvisionedContext
        ? await createProvisionedJobRoleVersion(Number(provisionedCompanyId), jobTaskId, payload)
        : useSystemVersionApis
        ? await createJobTaskVersion({
            ...payload,
            jobTaskId: Number(jobTaskId),
          })
        : await createCompanyJobRoleVersion(jobTaskId, payload);
      if (!result.error && !result.statusCode) {
        closeModal();
        refreshVersions();
        return result;
      }
      return result;
    },
    [jobTaskId, closeModal, isProvisionedContext, provisionedCompanyId, refreshVersions, useSystemVersionApis],
  );

  const [updateState, updateVersion] = useAsyncFn(
    async (id, payload) => {
      const result = isProvisionedContext
        ? await updateProvisionedJobRoleVersion(Number(provisionedCompanyId), jobTaskId, id, payload)
        : useSystemVersionApis
        ? await updateJobTaskVersion(id, payload)
        : await updateCompanyJobRoleVersion(jobTaskId, id, payload);
      if (!result.error && !result.statusCode) {
        closeModal();
        refreshVersions();
        return result;
      }
      return result;
    },
    [closeModal, isProvisionedContext, jobTaskId, provisionedCompanyId, refreshVersions, useSystemVersionApis],
  );

  const [lifecycleState, runLifecycleAction] = useAsyncFn(
    async (id, action) => {
      const result = isProvisionedContext
        ? action === 'launch'
          ? await launchProvisionedJobRoleVersion(Number(provisionedCompanyId), jobTaskId, id)
          : await cancelProvisionedJobRoleVersion(Number(provisionedCompanyId), jobTaskId, id)
        : action === 'launch'
        ? useSystemVersionApis
          ? await launchJobTaskVersion(id)
          : await launchCompanyJobRoleVersion(jobTaskId, id)
        : useSystemVersionApis
        ? await cancelJobTaskVersion(id)
        : await cancelCompanyJobRoleVersion(jobTaskId, id);
      if (!result.error && !result.statusCode) {
        closeModal();
        refreshVersions();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [closeModal, isProvisionedContext, jobTaskId, provisionedCompanyId, refreshVersions, showErrorModal, useSystemVersionApis],
  );

  const confirmLifecycleAction = useCallback(
    (id, action) => {
      openEntityLifecycleConfirm({
        dispatch,
        closeModal,
        t,
        i18nPrefix: 'jobTaskVersions',
        action,
        onConfirm: () => runLifecycleAction(id, action),
      });
    },
    [closeModal, dispatch, runLifecycleAction, t],
  );

  const [deleteState, removeVersion] = useAsyncFn(
    async id => {
      const result = isProvisionedContext
        ? await deleteProvisionedJobRoleVersion(Number(provisionedCompanyId), jobTaskId, id)
        : useSystemVersionApis
        ? await deleteJobTaskVersion(id)
        : await deleteCompanyJobRoleVersion(jobTaskId, id);
      if (!result.error && !result.statusCode) {
        closeModal();
        refreshVersions();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [closeModal, isProvisionedContext, jobTaskId, provisionedCompanyId, refreshVersions, showErrorModal, useSystemVersionApis],
  );

  const openCreateModal = useCallback(() => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('jobTaskVersions.addModalTitle'),
          customStyles: classes.versionModal,
        },
        <JobTaskVersionForm
          mode="create"
          submitting={createState.loading}
          onCancel={closeModal}
          onSubmitForm={createVersion}
        />,
      ),
    );
  }, [closeModal, createState.loading, createVersion, dispatch, t, classes.versionModal]);

  const openRequirementsPage = useCallback(
    version => {
      const pathname = isProvisionedContext
        ? buildProvisionedRoleVersionRequirementsPath(provisionedCompanyId, jobTaskId, version.id)
        : `${routesAliases.editJobTaskVersionRequirements}/${jobTaskId}/versions/${version.id}/requirements`;
      history.push({
        pathname,
        state: history.location.state,
      });
    },
    [history, isProvisionedContext, jobTaskId, provisionedCompanyId],
  );

  const openEditModal = useCallback(
    async version => {
      const requirementsResult = isProvisionedContext
        ? await getProvisionedJobRoleVersionRequirements(Number(provisionedCompanyId), jobTaskId, version.id)
        : useSystemVersionApis
        ? await getJobTaskVersionRequirements(version.id)
        : await getCompanyJobRoleVersionRequirements(jobTaskId, version.id);
      const canLaunch =
        !requirementsResult?.error && !requirementsResult?.statusCode && (requirementsResult?.modules || []).length > 0;

      dispatch(
        modalActions.openModal(
          {
            size: 'medium',
            title: t('jobTaskVersions.editModalTitle'),
            customStyles: classes.versionModal,
          },
          <JobTaskVersionForm
            mode="edit"
            initialValues={version}
            submitting={updateState.loading || lifecycleState.loading}
            onCancel={closeModal}
            onSubmitForm={payload => updateVersion(version.id, payload)}
            onLaunch={() => confirmLifecycleAction(version.id, 'launch')}
            onCancelEntity={() => confirmLifecycleAction(version.id, 'cancel')}
            canLaunch={canLaunch}
          />,
        ),
      );
    },
    [
      closeModal,
      dispatch,
      t,
      updateState.loading,
      lifecycleState.loading,
      updateVersion,
      confirmLifecycleAction,
      classes.versionModal,
      useSystemVersionApis,
      isProvisionedContext,
      provisionedCompanyId,
      jobTaskId,
    ],
  );

  const handleDeleteVersion = useCallback(
    versionId => {
      const [config, element] = getConfirmationModalConfig({
        title: t('jobTaskVersions.deleteModalTitle'),
        confirmAction: () => removeVersion(versionId),
        rejectAction: closeModal,
        message: t('jobTaskVersions.deleteConfirmationMessage'),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removeVersion, t],
  );

  const handleColumnSorting = useCallback((field, dir) => {
    setSorting({ field, dir });
  }, []);

  const items = versionsState.value?.items || [];
  const findVersionById = useCallback(versionId => items.find(item => item.id === Number(versionId)), [items]);
  const isVersionDeletable = useCallback(version => DELETABLE_VERSION_STATUSES.includes(version?.status), []);
  const hasDraftVersion = items.some(item => item.status === 'draft');

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'playlist_add_check',
        title: t('jobTaskVersionRequirements.actionConfigure'),
        onClick: event => {
          const version = findVersionById(event.currentTarget.dataset.key);
          if (version) {
            openRequirementsPage(version);
          }
        },
      },
      {
        type: 'icon',
        icon: 'edit',
        onClick: event => {
          const version = findVersionById(event.currentTarget.dataset.key);
          if (version) {
            openEditModal(version);
          }
        },
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        onClick: event => {
          const version = findVersionById(event.currentTarget.dataset.key);
          if (!isVersionDeletable(version)) {
            return;
          }
          handleDeleteVersion(Number(event.currentTarget.dataset.key));
        },
        getDisabled: rowData => !isVersionDeletable(findVersionById(rowData.id)),
        getTitle: rowData =>
          isVersionDeletable(findVersionById(rowData.id)) ? undefined : t('jobTaskVersions.deleteDisabledForStatus'),
      },
    ],
    [findVersionById, handleDeleteVersion, isVersionDeletable, openEditModal, openRequirementsPage, t],
  );

  if (!canViewVersions) {
    return null;
  }

  const [columns, rows] = transformTableData(tableConfig, items);
  const isLoading = versionsState.loading || deleteState.loading;

  return (
    <Box mt={6}>
      {isFromSystem ? <CompanySyncPanel entityType="jobRole" entityId={jobTaskId} onSynced={refreshVersions} /> : null}
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t('jobTaskVersions.sectionTitle')}
        </Typography>
        {canMutateVersions ? (
          <Tooltip
            title={hasDraftVersion ? t('jobTaskVersions.addDisabledWhileDraft') : ''}
            classes={{ tooltip: classes.addVersionTooltip }}
          >
            <span>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Icon>add</Icon>}
                onClick={openCreateModal}
                disabled={hasDraftVersion}
              >
                {t('jobTaskVersions.actionAdd')}
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Box>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={items.length}
          rowsPerPage={items.length || 10}
          actions={
            canMutateVersions
              ? tableActions
              : tableActions.filter(action => action.icon !== 'delete' && action.icon !== 'edit')
          }
          onColumnSorting={handleColumnSorting}
        />
      </ModalBusyContainer>
    </Box>
  );
};

JobTaskVersionsTable.propTypes = {
  jobTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isFromSystem: PropTypes.bool,
  provisionedCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

JobTaskVersionsTable.defaultProps = {
  isFromSystem: false,
  provisionedCompanyId: null,
};

export default JobTaskVersionsTable;
